/* eslint-disable linebreak-style */
import React, { useContext } from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import {
  Stack, StackItem, ActionButton, IContextualMenuProps, DirectionalHint,
  IconButton,
} from '@fluentui/react';
import {
  UnauthenticatedTemplate, AuthenticatedTemplate, useMsal, useAccount,
} from '@azure/msal-react';
import { DeveloperPanel } from '../DeveloperPanel';
import { useToken } from '../Auth';
import { useLLMSetting } from '../Contexts';
import { brandLogo } from '../../Assets';
import { Logo as VivaraLogo } from '../Logo';
import { useIsVivara } from '../../hooks/useIsVivara';
import { useIsDevMode } from '../../hooks/useIsDevMode';
import { UserAvatar } from '../Logo/UserAvatar';
import { PlaygroundTheme } from '../../Theme';
import { ChatManagerContext } from '../ChatManagerContext';

export const defaultHeaderHeight: number = 125;

const useStyles = createUseStyles<string, unknown, PlaygroundTheme>((theme) => ({
  appHeader: {
    width: '100%',
    '&.is-vivara': {
      height: 66,
      '& .user-avatar svg': {
        cursor: 'pointer',
      },
    },
  },
  headerContainer: {
    position: 'relative',
    paddingLeft: '16px',
    paddingRight: '16px',
    minHeight: defaultHeaderHeight,
    backgroundColor: theme.headerBackground,
    '-webkit-box-shadow': `0px 0px 60px 30px ${theme.headerBackground}`, /* WebKit */
    '-moz-box-shadow': `0px 0px 60px 30px ${theme.headerBackground}`, /* Firefox */
    boxShadow: `0px 0px 60px 30px ${theme.headerBackground}`, /* Standard */
    zIndex: 10000,
    '&.is-vivara': {
      minHeight: 'auto',
    },
  },
  headerText: {
    color: '#323130',
    fontSize: '20px',
    '&.is-vivara': {
      display: 'none',
    },
  },
  headerButton: {
    color: '#323130',
    fontSize: '20px',
    '&.is-vivara': {
      '&:hover, &:active': {
        color: theme.themePrimary,
      },
    },
  },
  brandLogo: {
    paddingLeft: '20px',
    display: 'flex',
    '& .title': {
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '30px',
      paddingLeft: '10px',
    },
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100px',
    marginRight: '20px',
    '&.is-vivara': {
      height: 'auto',
    },
  },
  iconButton: {
    '& i': {
      fontSize: '30px',
      height: '30px',
      lineHeight: '30px',
      color: theme.neutralPrimary,
    },
    '&:hover': {
      background: 'none',
      scale: '1.1',
    },
    '&.is-vivara': {
      '& i': {
        color: theme.themePrimary,
        fontSize: 20,
      },
    },
  },
}));

export const Header: React.FunctionComponent = () => {
  const isVivara = useIsVivara();
  const isDevMode = useIsDevMode();
  const classes = useStyles();
  const {
    refreshInteractively,
    logout,
  } = useToken();
  const { accounts } = useMsal();
  const account = useAccount(accounts[0] || {});
  const { skipDevloperPanelWhenFirstRender } = useLLMSetting();
  const { resetTopics } = useContext(ChatManagerContext)!;

  const onLogin = React.useCallback(() => {
    refreshInteractively(true);
  }, [refreshInteractively]);

  const vivaraLogoutMenuProps = React.useMemo(() => ({
    directionalHint: DirectionalHint.bottomCenter,
    styles: {
      root: {
        minWidth: 100,
      },
    },
  }), []);

  const logoutMenuProps = React.useMemo<IContextualMenuProps>(() => ({
    shouldFocusOnMount: true,
    items: [{
      key: 'logout',
      text: 'Log out',
      onClick: () => logout(),
    }],
    ...(isVivara ? vivaraLogoutMenuProps : {}),
  }), [isVivara, vivaraLogoutMenuProps, logout]);

  const headerClassName = classNames(isVivara && 'is-vivara', classes.appHeader);
  const [isDeveloperPanelOpen, setIsDeveloperPanelOpen] = React.useState(!skipDevloperPanelWhenFirstRender);

  return (
    <header className={headerClassName}>
      <Stack
        horizontal
        horizontalAlign="space-between"
        verticalAlign="center"
        className={classNames(classes.headerContainer, isVivara && 'is-vivara')}
      >
        <StackItem className={classes.brandLogo}>
          {isVivara ? (
            <>
              <VivaraLogo size="small" text="LOGO" onClick={resetTopics} />
              <span className="title">Vivara</span>
            </>
          ) : brandLogo}
        </StackItem>
        <StackItem>
          <UnauthenticatedTemplate>
            <ActionButton
              className={classNames('log-in-button', classes.headerText)}
              onClick={onLogin}
            >
              Login
            </ActionButton>
          </UnauthenticatedTemplate>
          <AuthenticatedTemplate>
            <Stack
              className={classNames(classes.buttonGroup, isVivara && 'is-vivara')}
              tokens={{ childrenGap: 16 }}
              horizontal
            >
              {
              // hide setting icon under vivara mode without dev mode
              (isVivara && !isDevMode) || (
                <IconButton
                  iconProps={{ iconName: 'Settings' }}
                  onClick={() => {
                    setIsDeveloperPanelOpen(true);
                  }}
                  className={classNames(isVivara && 'is-vivara', classes.iconButton)}
                />
              )
              }
              <ActionButton
                className={classNames('logged-in-username', isVivara && 'is-vivara', classes.headerButton)}
                menuProps={logoutMenuProps}
                menuIconProps={{
                  iconName: 'ChevronDown',
                  className: classNames(classes.headerText, isVivara && 'is-vivara'),
                }}
              >
                {isVivara ? <UserAvatar /> : account?.username}
              </ActionButton>
            </Stack>
            <DeveloperPanel
              isOpen={isDeveloperPanelOpen}
              onClose={() => {
                setIsDeveloperPanelOpen(false);
              }}
            />
          </AuthenticatedTemplate>
        </StackItem>
      </Stack>
    </header>
  );
};
